import React from 'react';
import { FaTiktok, FaFacebookF, FaGoogle, FaLinkedinIn, FaMicrosoft, FaSnapchatGhost } from 'react-icons/fa';
import { SiPinterest, SiX, SiReddit, SiAmazon, SiSpotify, SiYoutube, SiShopify, SiEtsy } from 'react-icons/si';
import logo from 'assets/images/logo.png';

const ACCENT = '#818CF8';        // periwinkle — used inside the dark ad cards
const ACCENT_DEEP = '#4F46E5';   // deeper indigo — used on the light hero background
const R1 = 175;   // inner orbit radius (connected platforms — ad cards)
const R2 = 245;   // outer orbit radius (roadmap chips)
const TILT = 16;  // orbital plane tilt in degrees

const TaboolaMark = () => (
  <span style={{ display: 'inline-flex', width: 14, height: 14, borderRadius: 4, background: '#1862D9', color: '#fff', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, fontFamily: 'Georgia,serif' }}>t</span>
);

// Inner orbit — the 6 connected platforms as live ad cards
const CONNECTED = [
  { icon: <FaTiktok />,        name: 'TikTok',   color: '#69C9D0', metric: '+12% CTR' },
  { icon: <FaFacebookF />,     name: 'Meta',     color: '#1877f2', metric: '3.0% CTR' },
  { icon: <FaGoogle />,        name: 'Google',   color: '#4285f4', metric: '3.4x ROAS' },
  { icon: <FaLinkedinIn />,    name: 'LinkedIn', color: '#0a66c2', metric: '+7% leads' },
  { icon: <FaSnapchatGhost />, name: 'Snapchat', color: '#FFFC00', metric: '-18% CPC' },
  { icon: <TaboolaMark />,     name: 'Taboola',  color: '#5B8DEF', metric: '+9% conv' },
];

// Outer orbit — roadmap platforms (part of the 27-platform roadmap)
const ROADMAP = [
  { icon: <SiPinterest /> },
  { icon: <SiX /> },
  { icon: <SiReddit /> },
  { icon: <SiAmazon /> },
  { icon: <SiSpotify /> },
  { icon: <SiYoutube /> },
  { icon: <FaMicrosoft /> },
  { icon: <SiShopify /> },
  { icon: <SiEtsy /> },
  { icon: <span style={{ fontFamily: 'Georgia,serif', fontWeight: 700, fontSize: 11 }}>Ob</span> },
  { icon: <span style={{ fontFamily: 'Georgia,serif', fontWeight: 700, fontSize: 11 }}>Cr</span> },
];

// Mini ad-card for a connected platform
function AdCard({ p, dur, delay }) {
  return (
    <div style={{
      width: 104,
      marginLeft: -52, marginTop: -38,
      borderRadius: 12,
      background: 'linear-gradient(165deg, rgba(30,25,62,0.97) 0%, rgba(15,11,45,0.97) 100%)',
      border: '1px solid rgba(255,255,255,0.15)',
      boxShadow: '0 14px 36px rgba(34,28,81,0.3)',
      padding: '8px 10px',
      backfaceVisibility: 'hidden',
      animation: `orbDepth ${dur}s linear infinite`,
      animationDelay: `${delay}s`,
    }}>
      {/* platform row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: p.color, display: 'flex', alignItems: 'center' }}>{p.icon}</span>
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk',sans-serif", letterSpacing: '0.02em' }}>{p.name}</span>
        <span style={{ marginLeft: 'auto', width: 4, height: 4, borderRadius: '50%', background: ACCENT, boxShadow: `0 0 5px ${ACCENT}` }} />
      </div>
      {/* faux ad creative */}
      <div style={{ height: 16, borderRadius: 5, background: `linear-gradient(120deg, ${p.color}33 0%, ${p.color}14 60%, rgba(255,255,255,0.05) 100%)`, border: '1px solid rgba(255,255,255,0.07)', marginBottom: 6, position: 'relative', overflow: 'hidden' }}>
        <span style={{ position: 'absolute', top: 3, right: 4, fontSize: 6, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.4)', fontFamily: "'Space Mono',monospace" }}>AD</span>
      </div>
      {/* skeleton + metric */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.14)', width: '85%', marginBottom: 3 }} />
          <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', width: '55%' }} />
        </div>
        <span style={{ fontSize: 8.5, fontWeight: 700, color: ACCENT, whiteSpace: 'nowrap' }}>{p.metric}</span>
      </div>
    </div>
  );
}

const PlatformOrbit = () => {
  const D1 = 36; // inner orbit period (s)
  const D2 = 64; // outer orbit period (s)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      <style>{`
        @keyframes orb3d {
          from { transform: rotateY(0deg)   translateZ(${R1}px) rotateY(0deg); }
          to   { transform: rotateY(360deg) translateZ(${R1}px) rotateY(-360deg); }
        }
        @keyframes orb3dRev {
          from { transform: rotateY(0deg)    translateZ(${R2}px) rotateY(0deg); }
          to   { transform: rotateY(-360deg) translateZ(${R2}px) rotateY(360deg); }
        }
        /* opacity only — animated filter/blur kicks elements out of Chrome's
           3D depth sorting (chips painted over cards, broken occlusion) */
        @keyframes orbDepth {
          0%, 100% { opacity: 1; }
          45%, 55% { opacity: 0.45; }
        }
        @keyframes orbDepthDim {
          0%, 100% { opacity: 0.9; }
          45%, 55% { opacity: 0.3; }
        }
        @keyframes ob-pulse {
          0%   { transform: translate(-50%,-50%) scale(0.6); opacity: 0.5; }
          100% { transform: translate(-50%,-50%) scale(1.6); opacity: 0; }
        }
        @keyframes obAbsorb1 {
          0%   { transform: rotateY(var(--a)) translateZ(${R1}px) scale(1); opacity: 0; }
          10%  { opacity: 0.9; }
          85%  { opacity: 0.8; }
          100% { transform: rotateY(var(--a)) translateZ(16px) scale(0.25); opacity: 0; }
        }
        /* perspective lives on .ob-layer (one per stage), NOT on the scene:
           a shared scene perspective + preserve-3d chains both stages into one
           3D context, letting the flat ring discs plane-split the ad cards */
        .ob-scene { position: relative; width: 560px; height: 430px; flex-shrink: 0; }
        .ob-layer { position: absolute; inset: 0; perspective: 900px; }
        @media (max-width: 1100px) { .ob-scene { transform: scale(0.8); margin: -42px 0; } }
        @media (max-width: 900px)  { .ob-scene { transform: scale(0.62); margin: -80px 0; } }
        @media (prefers-reduced-motion: reduce) {
          .ob-stage, .ob-stage * { animation-play-state: paused !important; }
        }
      `}</style>

      <div className="ob-scene">
        <div className="ob-layer">
        <div className="ob-stage" style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transform: `rotateX(${TILT}deg)` }}>

          {/* Orbital plane guides (render as ellipses thanks to the tilt) */}
          {[{ r: R1, dash: false }, { r: R2, dash: true }].map((g, i) => (
            <div key={i} style={{
              position: 'absolute', top: '50%', left: '50%',
              width: g.r * 2, height: g.r * 2,
              margin: `-${g.r}px 0 0 -${g.r}px`,
              borderRadius: '50%',
              border: g.dash ? '1px dashed rgba(79,70,229,0.22)' : '1px solid rgba(79,70,229,0.18)',
              transform: 'rotateX(90deg)',
              pointerEvents: 'none',
            }} />
          ))}

          {/* Pulsing ripples on the orbital plane */}
          {[0, 1].map(i => (
            <div key={i} style={{
              position: 'absolute', top: '50%', left: '50%',
              width: 130, height: 130, borderRadius: '50%',
              border: '1.5px solid rgba(79,70,229,0.35)',
              transform: 'translate(-50%,-50%) rotateX(90deg)',
              animation: `ob-pulse 3.6s ${i * 1.8}s ease-out infinite`,
              pointerEvents: 'none',
            }} />
          ))}

          {/* Absorption sparks — ads flying along the plane into the center */}
          {CONNECTED.map((p, i) => (
            <div key={`dot-${i}`} style={{
              position: 'absolute', top: '50%', left: '50%',
              width: 8, height: 8, marginTop: -4, marginLeft: -4,
              borderRadius: 3,
              background: ACCENT_DEEP,
              boxShadow: `0 0 10px ${ACCENT_DEEP}`,
              '--a': `${i * 60}deg`,
              animation: `obAbsorb1 4.4s ${i * 1.1}s ease-in infinite`,
              pointerEvents: 'none',
            }} />
          ))}
        </div>
        </div>

        {/* Foreground 3D layer: center logo + orbiting cards/chips.
            Own perspective wrapper = own 3D rendering context, so the flat
            rotateX(90deg) discs in the background layer can never plane-split
            the cards (cards render sliced in half otherwise). */}
        <div className="ob-layer">
        <div className="ob-stage" style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transform: `rotateX(${TILT}deg)` }}>

          {/* Center: OneBotAds. NOT counter-tilted: it must stay parallel to the
              billboarded cards — a tilted pill plane intersects passing cards and
              Chrome plane-splits them (top halves vanish). The 16° stage tilt on
              a small pill is visually negligible. */}
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
            <div style={{
              background: '#fff', border: '1px solid #E5E6EF', borderRadius: 16, padding: '12px 16px',
              boxShadow: '0 0 50px rgba(79,70,229,0.28), 0 12px 30px rgba(34,28,81,0.18)',
            }}>
              <img src={logo} alt="OneBotAds" style={{ height: 26, display: 'block' }} />
            </div>
          </div>

          {/* Inner orbit — ad cards with real depth */}
          {CONNECTED.map((p, i) => (
            <div key={i} style={{
              position: 'absolute', top: '50%', left: '50%',
              width: 0, height: 0,
              animation: `orb3d ${D1}s linear infinite`,
              animationDelay: `${-(i * D1 / 6)}s`,
            }}>
              <AdCard p={p} dur={D1} delay={-(i * D1 / 6)} />
            </div>
          ))}

          {/* Outer orbit — roadmap chips, counter-rotating, dimmer */}
          {ROADMAP.map((p, i) => (
            <div key={`r-${i}`} style={{
              position: 'absolute', top: '50%', left: '50%',
              width: 0, height: 0,
              animation: `orb3dRev ${D2}s linear infinite`,
              animationDelay: `${-(i * D2 / ROADMAP.length)}s`,
            }}>
              <div style={{
                width: 32, height: 32, marginTop: -16, marginLeft: -16,
                borderRadius: 10,
                background: '#fff',
                border: '1px solid #E5E6EF',
                boxShadow: '0 6px 16px rgba(34,28,81,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, color: '#6B7280',
                backfaceVisibility: 'hidden',
                animation: `orbDepthDim ${D2}s linear infinite`,
                animationDelay: `${-(i * D2 / ROADMAP.length)}s`,
              }}>
                {p.icon}
              </div>
            </div>
          ))}
        </div>
        </div>
      </div>

      <div className="mono-label" style={{ color: '#A3A3AD', fontSize: 10 }}>
        <span style={{ color: ACCENT_DEEP }}>06 CONNECTED</span>
        {'  ·  '}+21 ON THE ROADMAP — 27 PLATFORMS
      </div>
    </div>
  );
};

export default PlatformOrbit;
